/**
 * Maps common CSS color names to hex values.
 * Only includes colors commonly used for team kits.
 */
const cssColorMap: Record<string, string> = {
    red: "#ff0000",
    blue: "#0000ff",
    green: "#008000",
    yellow: "#ffff00",
    white: "#ffffff",
    black: "#000000",
    orange: "#ff8800",
    purple: "#6600ff",
    pink: "#cc0ffa",
    navy: "#000080",
    maroon: "#800000",
    gold: "#ffd700",
    grey: "#808080",
    gray: "#808080",
    silver: "#c0c0c0",
    teal: "#008080",
    lime: "#00ff00",
    cyan: "#00ffff",
    brown: "#3a2c09",
    olive: "#808000",
    coral: "#ff7f50",
    crimson: "#dc143c",
    indigo: "#4b0082",
    violet: "#ee82ee",
    beige: "#f5f5dc",
    turquoise: "#40e0d0",
    aqua: "#00ffff",
    magenta: "#ff00ff",
    salmon: "#fa8072",
    khaki: "#f0e68c",
    lavender: "#6464f3",
    tan: "#d2b48c",
    ivory: "#fffff0",
    azure: "#f0ffff",
    plum: "#dda0dd",
    chartreuse: "#7fff00",
    skyblue: "#87ceeb",
};

/**
 * Maps non-English color names to their English equivalent.
 * Covers the ~15 most common kit colors per language.
 */
const colorTranslations: Record<string, Record<string, string>> = {
    da: {
        rød: "red",
        blå: "blue",
        grøn: "green",
        gul: "yellow",
        hvid: "white",
        sort: "black",
        orange: "orange",
        lilla: "purple",
        pink: "pink",
        guld: "gold",
        grå: "grey",
        brun: "brown",
        lyseblå: "cyan",
        mørkeblå: "navy",
        sølv: "silver",
    },
    de: {
        rot: "red",
        blau: "blue",
        grün: "green",
        gelb: "yellow",
        weiß: "white",
        weiss: "white",
        schwarz: "black",
        orange: "orange",
        lila: "purple",
        rosa: "pink",
        gold: "gold",
        grau: "grey",
        braun: "brown",
        dunkelblau: "navy",
        silber: "silver",
        türkis: "teal",
    },
    es: {
        rojo: "red",
        azul: "blue",
        verde: "green",
        amarillo: "yellow",
        blanco: "white",
        negro: "black",
        naranja: "orange",
        morado: "purple",
        púrpura: "purple",
        rosa: "pink",
        dorado: "gold",
        gris: "grey",
        marrón: "brown",
        granate: "maroon",
        plata: "silver",
    },
    fr: {
        rouge: "red",
        bleu: "blue",
        vert: "green",
        jaune: "yellow",
        blanc: "white",
        noir: "black",
        orange: "orange",
        violet: "purple",
        rose: "pink",
        or: "gold",
        gris: "grey",
        marron: "brown",
        bordeaux: "maroon",
        argent: "silver",
        marine: "navy",
    },
    he: {
        אדום: "red",
        כחול: "blue",
        ירוק: "green",
        צהוב: "yellow",
        לבן: "white",
        שחור: "black",
        כתום: "orange",
        סגול: "purple",
        ורוד: "pink",
        זהב: "gold",
        אפור: "grey",
        חום: "brown",
        כסף: "silver",
    },
    ja: {
        赤: "red",
        青: "blue",
        緑: "green",
        黄色: "yellow",
        白: "white",
        黒: "black",
        オレンジ: "orange",
        紫: "purple",
        ピンク: "pink",
        金: "gold",
        灰色: "grey",
        茶色: "brown",
        紺: "navy",
        銀: "silver",
    },
    nb: {
        rød: "red",
        blå: "blue",
        grønn: "green",
        gul: "yellow",
        hvit: "white",
        svart: "black",
        oransje: "orange",
        lilla: "purple",
        rosa: "pink",
        gull: "gold",
        grå: "grey",
        brun: "brown",
        mørkeblå: "navy",
        sølv: "silver",
    },
    nl: {
        rood: "red",
        blauw: "blue",
        groen: "green",
        geel: "yellow",
        wit: "white",
        zwart: "black",
        oranje: "orange",
        paars: "purple",
        roze: "pink",
        goud: "gold",
        grijs: "grey",
        bruin: "brown",
        marineblauw: "navy",
        zilver: "silver",
    },
    pl: {
        czerwony: "red",
        niebieski: "blue",
        zielony: "green",
        żółty: "yellow",
        biały: "white",
        czarny: "black",
        pomarańczowy: "orange",
        fioletowy: "purple",
        różowy: "pink",
        złoty: "gold",
        szary: "grey",
        brązowy: "brown",
        granatowy: "navy",
        srebrny: "silver",
    },
    pt: {
        vermelho: "red",
        azul: "blue",
        verde: "green",
        amarelo: "yellow",
        branco: "white",
        preto: "black",
        laranja: "orange",
        roxo: "purple",
        rosa: "pink",
        dourado: "gold",
        cinza: "grey",
        castanho: "brown",
        marrom: "brown",
        prata: "silver",
    },
};

/**
 * Flattened lookup: all translated color names -> hex value.
 * Built once at module load by merging all locale maps.
 */
const allColorNames: Record<string, string> = buildColorLookup();

function buildColorLookup(): Record<string, string> {
    // Translations first, then English on top — so English names always win on collision
    const result: Record<string, string> = {};
    for (const localeMap of Object.values(colorTranslations)) {
        for (const [translatedName, englishName] of Object.entries(localeMap)) {
            if (cssColorMap[englishName]) {
                result[translatedName] = cssColorMap[englishName];
            }
        }
    }
    // English CSS names applied last to take precedence over any collisions
    Object.assign(result, cssColorMap);
    return result;
}

const HEX_3_REGEX = /^#[0-9a-fA-F]{3}$/;
const HEX_6_REGEX = /^#[0-9a-fA-F]{6}$/;

/**
 * Normalizes a color value to a hex string.
 *
 * - If already a valid hex string (#RGB or #RRGGBB), returns it as-is.
 * - If a recognized color name (English or any supported language), returns the hex value.
 * - Returns undefined if the color is not recognized.
 *
 * @param value - The color string to normalize
 * @returns The hex color string, or undefined if not recognized
 */
export function normalizeColor(
    value: string | undefined | null
): string | undefined {
    if (!value || typeof value !== "string") {
        return undefined;
    }

    const trimmed = value.trim();
    if (trimmed === "") {
        return undefined;
    }

    // Already a valid hex color
    if (HEX_3_REGEX.test(trimmed) || HEX_6_REGEX.test(trimmed)) {
        return trimmed;
    }

    const lower = trimmed.toLowerCase();

    return allColorNames[lower];
}
